/** The hall of fame.
 *
 * A leaf like every other screen here: it is handed rows and renders them. It
 * does not compute a score, does not know where the table is kept, and does not
 * know what a game is - the host does all three, because a score is a *host*
 * fact. Nothing in a `GameState` says how hard the computer was playing or how
 * long the player took in wall-clock terms, and both belong on this table.
 *
 * The empty state matters more here than on most screens. A hall of fame with
 * nothing in it is the first thing a new player sees when he presses the plate,
 * and "no scores yet" with no explanation reads as a broken screen.
 *
 * What this screen is, materially, is a **ledger**: a bound sheet of ruled
 * parchment with the standings written on it. The previous pass drew it as a
 * content-height card pinned to the top of the body - the body centres on its
 * main axis, which is horizontal, so nothing ever stretched it downwards - on a
 * cold blue-grey wash that appears nowhere else in the game. On a 1920x1080 shot
 * that left most of the window empty below six rows. A hall of fame that
 * occupies a corner of its own screen is not a hall. It now fills the body, the
 * ruling continues past the last entry, and the empty state fills the same frame
 * so that a table with nothing in it is still a *page* rather than a strip.
 */

import { BitmapText } from "../theme/bitmapText.js";
import { useAtlas } from "../hooks/useAtlas.js";
import { usePhoneSheetFit } from "../theme/phone.js";
import { usePolishStyles } from "./polish.js";
import { MaybeIcon, ScreenStyles, useMeasure } from "./primitives.js";
import { FACTIONS, FORM_ART_CSS, crestKey } from "./formArt.js";
import { groupDigits, titleCase } from "./format.js";
import { playerColour } from "../theme/tokens.js";
import {
  BP, DIFFICULTY_MULTIPLIER_BP, PAR_DAY, finalScore, outcomeShareBp, speedBonusBp,
} from "./scoreModel.js";
import { StyleLayer, useStyleSheet } from "../theme/styleSheet.js";

export interface HighScoreRow {
  /** Milliseconds since the epoch; the host's clock, not a game fact. */
  readonly at: number;
  readonly score: number;
  readonly outcome: "victory" | "defeat" | "draw";
  /** The player's own colour, for the swatch. */
  readonly colour: string;
  readonly faction: string;
  readonly difficulty: string;
  /** Absolute day the game ended on. */
  readonly day: number;
  readonly mapCells: number;
  readonly opponents: number;
  readonly condition: string;
}

export interface HighScoresProps {
  readonly rows: readonly HighScoreRow[];
  /** Marked so a player can find the game he has just finished. */
  readonly highlightAt?: number | null;
  readonly onClear?: () => void;
  readonly onClose: () => void;
}

const OUTCOME_LABEL: Readonly<Record<HighScoreRow["outcome"], string>> = Object.freeze({
  victory: "Won",
  defeat: "Lost",
  draw: "Drawn",
});

/** A date the player recognises, without `Intl`: the kernel bans it and the
 *  shell has no reason to want a different answer per machine. */
export function shortDate(at: number): string {
  const d = new Date(at);
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${String(d.getFullYear())}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * The line under the score, in the order a player reads it.
 *
 * The ids arrive as the host holds them - `castle`, `impossible` - and printing
 * them raw put lowercase machine words in the middle of a serif sentence on the
 * shipped screenshot. Nothing else on this screen knows how to spell them, so
 * it is done here and only here.
 */
export function scoreLine(row: HighScoreRow): string {
  return `${OUTCOME_LABEL[row.outcome]} on day ${String(row.day)}`
    + ` · ${titleCase(row.faction)}`
    + ` · ${titleCase(row.difficulty)}`
    + ` · ${String(row.mapCells)}×${String(row.mapCells)}`
    + ` · ${String(row.opponents)} opponent${row.opponents === 1 ? "" : "s"}`;
}

/** Gold, silver, bronze, then plain ink. A ranked table that ranks nothing
 *  visually is a list. */
const RANK_CLASS: readonly string[] = Object.freeze(["hm-hof__rank--1", "hm-hof__rank--2", "hm-hof__rank--3"]);

// -------------------------------------------------------------- empty page

/** A basis-point multiplier as a player would write it: `1.60`, not `16000`. */
const times = (bp: number): string => (bp / BP).toFixed(2);

interface RuleRow { readonly label: string; readonly factor: string }
interface RuleColumn { readonly head: string; readonly rows: readonly RuleRow[] }

/**
 * What a ledger with nothing in it says instead.
 *
 * Every figure below is computed from `scoreModel.ts` rather than typed out, so
 * the page cannot promise a ladder the formula does not pay. That matters more
 * here than the saving in characters: this table is the whole content of the
 * screen for as long as a player has not finished a game, which on a fresh
 * browser is always - and a printed multiplier that has drifted from the code
 * is a worse defect than an empty page.
 *
 * Three days rather than a curve, because the speed bonus is linear to par and
 * flat after it, and three points state that: double on day one, par at 120,
 * and no further loss beyond. `speedBonusBp` is asked for all three.
 */
export const SCORE_RULES: readonly RuleColumn[] = Object.freeze([
  {
    head: "How it ended",
    rows: Object.freeze([
      { label: "Won", factor: times(outcomeShareBp("victory")) },
      { label: "Adjudicated", factor: times(outcomeShareBp("draw")) },
      { label: "Lost", factor: times(outcomeShareBp("defeat")) },
    ]),
  },
  {
    head: "Who you played",
    rows: Object.freeze(
      (["easy", "normal", "hard", "impossible"] as const).map((id) => ({
        label: titleCase(id),
        factor: times(DIFFICULTY_MULTIPLIER_BP[id] ?? BP),
      })),
    ),
  },
  {
    head: "How long it took",
    rows: Object.freeze([
      { label: "Day 1", factor: times(speedBonusBp(1)) },
      { label: `Day ${String(PAR_DAY / 2)}`, factor: times(speedBonusBp(PAR_DAY / 2)) },
      { label: `Day ${String(PAR_DAY)} or later`, factor: times(speedBonusBp(PAR_DAY)) },
    ]),
  },
]);

/** The worked example the formula's own comment promises, computed. */
export function workedExample(): string {
  const kingdom = 9000;
  const day = PAR_DAY / 2;
  const total = finalScore({ kingdom, day, difficulty: "normal", outcome: "victory" });
  return `A kingdom worth ${groupDigits(kingdom)} on Normal, won on day ${String(day)}: `
    + `${groupDigits(kingdom)} × ${times(speedBonusBp(day))} × ${times(BP)} × ${times(BP)}`
    + ` = ${groupDigits(total)}.`;
}

/** The twelve keeps as a band across the page.
 *
 * The one thing a hall of fame with nothing in it can honestly show is who can
 * be entered in it, and this build already draws all twelve - they are the
 * paintings the new game screen's faction picker is made of, and the keep is
 * the frame each of those is titled by. Two rows on a laptop, one on a desktop;
 * the pack draws every one of them 225px wide on a common ground line, so a
 * frieze needs nothing but a shared box height to sit straight. */
function CrestFrieze(): JSX.Element {
  const [ref, box] = useMeasure<HTMLDivElement>();
  // The pack draws every keep 225x242. A 236px box fits that 1:1 and a 174px
  // box at exactly half; there is nothing useful between the two, because
  // `fitSpriteBox` only ever draws a whole multiple or the reciprocal of a
  // whole divisor and a box of 200 would draw the same 112px sprite as 174
  // with 88px of air round it. Six to a row either way, so the band is the
  // same composition at both sizes and only the plate changes.
  const size = box.w >= 1440 ? 236 : 174;
  return (
    <div className="hm-hof__crests" ref={ref} aria-hidden="true">
      {FACTIONS.map((f) => (
        <span className="hm-hof__crest" key={f}>
          <MaybeIcon atlasKey={crestKey(f)} size={size} boxHeight={size} />
          <span className="hm-hof__crestname">{titleCase(f)}</span>
        </span>
      ))}
    </div>
  );
}

export function HighScores(props: HighScoresProps): JSX.Element {
  usePolishStyles();
  useHallStyles();
  const atlas = useAtlas();
  const rows = props.rows;

  const fit = usePhoneSheetFit();
  return (
    <div className={`hs-screen hm-hof-screen ${fit.screenClass}`.trim()} ref={fit.screenRef}
      style={fit.style} role="region" aria-label="High scores">
      <ScreenStyles />

      <div className="hs-phone-sheet" ref={fit.sheetRef}>
      <div className="hs-screen__bar hs-plank">
        {/* The plank does not repeat the page's own name. "HALL OF FAME" here
            and "THE HALL OF FAME" 80px below it on the leaf is the same words
            twice in one glance, and the leaf is where the name belongs - it is
            the ornament, the plank is the chrome. The region's aria-label
            still carries the name for anything that cannot see the leaf. */}
        {/* A struck plaque, not a bare sentence. This shipped as `.hs-screen__sub`
            alone at the far left of an otherwise empty plank - 12px of grey
            serif with nothing around it, on the one screen in the build with no
            title in its own bar - and an art director read it as body copy left
            outside a frame. The words are the same; they are now set the way
            every other small label on this build is set. */}
        <span className="hm-hof__note">
          {rows.length === 0
            ? "Kept in this browser"
            : `${String(rows.length)} game${rows.length === 1 ? "" : "s"} · kept in this browser`}
        </span>
        <span className="hs-spacer" />
        {props.onClear !== undefined && rows.length > 0 ? (
          <button
            type="button"
            className="hs-plate hs-plate--sm"
            title="Empty the table. There is no undo."
            onClick={props.onClear}
          >
            Clear
          </button>
        ) : null}
        <button type="button" className="hs-plate hs-plate--sm" onClick={props.onClose}>Close</button>
      </div>

      <div className="hs-screen__body hm-hof">
        <div className="hm-hof__book">
          <div className="hm-hof__leaf">
            <div className="hm-hof__head">The Hall of Fame</div>
            {/* The ruling is printed whether or not anything has been entered,
                because that is the difference between a blank ledger and an
                unfinished panel - and the blank one is the state a fresh
                browser always shows. The COLUMN HEADS are not: four heads over
                ten rules and no rows is a table promising rows that do not
                exist, which is how this page read to a critic. With nothing
                entered the page prints the scoring ladder instead, and the
                ladder brings its own heads. */}
            {rows.length === 0 ? null : (
              <div className="hm-hof__cols" aria-hidden="true">
                <span className="hm-hof__col hm-hof__col--rank">Rank</span>
                <span className="hm-hof__col hm-hof__col--score">Score</span>
                <span className="hm-hof__col hm-hof__col--game">Game</span>
                <span className="hm-hof__col hm-hof__col--date">Finished</span>
              </div>
            )}
            <div className={`hm-hof__rows hs-scroll ${rows.length === 0 ? "hm-hof__rows--empty" : ""}`}>
              {rows.length === 0 ? (
                <div className="hm-hof__empty">
                  {/* The ruling of an empty page is drawn on these two bands
                      rather than behind the writing; see `.hm-hof__rule`. A
                      modifier class rather than `:has()` - an unsupported
                      selector fails open, and failing open here means rules
                      through the writing on the page. */}
                  <span className="hm-hof__rule hm-hof__rule--over" aria-hidden="true" />
                  {/* The picture this page was missing. It measured 0.00% blank
                      by the 32x32 luminance scan while two thirds of its
                      parchment held nothing at all - the fine hatch in the
                      paper's fibre satisfies that test at any coverage - and
                      the honest thing to put in the space is who the ledger is
                      for. */}
                  <CrestFrieze />
                  <p className="hm-hof__crestcap">
                    The twelve houses. Whoever finishes a game is entered here under his own.
                  </p>
                  <div className="hm-hof__emptyhead">Nothing is entered yet</div>
                  <p className="hm-hof__emptybody">
                    A game is scored when it ends — won, lost or adjudicated. The score
                    is what your kingdom was worth on the last day, multiplied three
                    times over: by the ending, by the opponent, and by the clock.
                  </p>
                  {/* The ladder itself, on the page it governs. An empty hall of
                      fame is the one screen in this build with nothing of the
                      player's own to show, so it shows the rule it will be
                      judging him by - which is both the honest thing to put
                      there and enough writing to make the leaf a page. */}
                  <div className="hm-hof__ladder">
                    {SCORE_RULES.map((col) => (
                      <div className="hm-hof__lcol" key={col.head}>
                        <div className="hm-hof__lhead">{col.head}</div>
                        {col.rows.map((r) => (
                          <div className="hm-hof__lrow" key={r.label}>
                            <span className="hm-hof__llabel">{r.label}</span>
                            <span className="hm-hof__lfactor">
                              <span className="hm-hof__ltimes">×</span>
                              <BitmapText scale={2} keyline="#f7edcf">{r.factor}</BitmapText>
                            </span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                  <p className="hm-hof__example">{workedExample()}</p>
                  <span className="hm-hof__rule hm-hof__rule--under" aria-hidden="true" />
                </div>
              ) : rows.map((r, i) => (
                <div
                  className={`hm-hof__row ${props.highlightAt === r.at ? "hm-hof__row--you" : ""}`}
                  key={`${String(r.at)}:${String(i)}`}
                >
                  {/* Every figure on this page is set in the bitmap face. A
                      ledger is read down its numeral columns, and anti-aliased
                      serif digits beside hard-quantised sprites are what marks
                      the whole build as the non-commercial one. */}
                  <span className={`hm-hof__rank ${RANK_CLASS[i] ?? ""}`}>
                    <BitmapText scale={2} keyline={i < 3 ? "#fff6d8" : "#241a08"}
                      title={`Rank ${String(i + 1)}`}>
                      {String(i + 1)}
                    </BitmapText>
                  </span>
                  <span
                    className="hm-hof__flag"
                    aria-hidden="true"
                    style={{ background: playerColour(r.colour) }}
                  />
                  {/* The keep the game was won from. The row already prints the
                      faction as a word in the line below the score; a ledger of
                      finished games that names twelve houses and draws none of
                      them is the same screen with the picture left out. */}
                  <span className="hm-hof__seat">
                    <MaybeIcon
                      atlasKey={atlas.has(crestKey(r.faction)) ? crestKey(r.faction) : null}
                      size={54}
                      boxHeight={48}
                      title={titleCase(r.faction)}
                    />
                  </span>
                  <span className={`hm-hof__score hm-hof__score--${r.outcome}`}>
                    <BitmapText scale={3} keyline="#f7edcf" title={`${groupDigits(r.score)} points`}>
                      {groupDigits(r.score)}
                    </BitmapText>
                  </span>
                  <span className="hm-hof__game">{scoreLine(r)}</span>
                  <span className="hm-hof__date">
                    <BitmapText scale={2} keyline="#f7edcf">{shortDate(r.at)}</BitmapText>
                  </span>
                </div>
              ))}
            </div>
            <div className="hm-hof__foot">
              {rows.length === 0
                ? "Finish a game and it is entered here."
                : "Kingdom worth on the last day, raised for a hard opponent and a short war."}
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ styles

const HALL_STYLE_ID = "hm-hall-of-fame";

const HALL_CSS = `
/* The room the ledger is read in, and the reason this screen is NOT
   \`hs-screen--sheet\` like Settings and Save/Load.
   Those two are dialogs: small enough that what shows through the scrim is
   still a picture. The ledger is not - it spans 1558 of 1920px at 1080p
   (measured on the shot), so opting in leaves 362px, 18.9% of the frame, of
   the adventure map and a vertically sliced hero sidebar down the two edges,
   which reads as a modal dropped on a live screen rather than as a hall.
   the classic's Hall of Fame is a painted screen, not a dialog. This is the
   same lit-from-above board the spellbook is opened on.
   (The shared \`.hs-screen\` ground is warm now, so the old note here about a
   cold blue-grey wash no longer described anything.) */
/* The board's own grain, at an amplitude that is actually a grain.
   It was rgba(255,220,170,0.03) over rgba(0,0,0,0.08), which on a ground this
   dark is about two luminance levels: the 32x32 scan measured every block of
   the 180px margin down each side of the ledger as flat, and by eye it was a
   ramp, not a board. Sampled the same way after, the same stripe at 0.055/0.10
   is 7 levels of standard deviation - a board you can see the cut of, still
   well under the ledger it carries. */
.hs-screen.hm-hof-screen {
  background-color: #24170c;
  /* The grain is listed ABOVE the vignette, not under it. Under it the 0.85
     black at the frame's edge multiplied a 14-level stripe down to two, so the
     board was flat again exactly where the vignette was strongest - which is
     the whole of the margin down each side of the ledger, and every one of
     those blocks measured blank. */
  background-image:
    repeating-linear-gradient(122deg,
      rgba(255,220,170,0.075) 0 2px,
      rgba(0,0,0,0.12) 2px 6px),
    repeating-linear-gradient(31deg,
      rgba(255,232,190,0.045) 0 1px,
      rgba(0,0,0,0.08) 1px 9px),
    radial-gradient(90% 70% at 50% 6%, rgba(255,214,150,0.12) 0%, rgba(0,0,0,0) 60%),
    radial-gradient(120% 95% at 50% 0%, rgba(0,0,0,0) 20%, rgba(0,0,0,0.85) 100%),
    linear-gradient(180deg, #3a2312 0%, #221408 58%, #120a04 100%);
}
.hs-screen__body.hm-hof {
  /* Stretch, not centre. A hall of fame that occupies a fifth of its own
     screen with a flat wash above and below it is the defect this replaces;
     the ledger is the screen. */
  align-items: stretch;
  justify-content: center;
  padding: 16px 20px 20px;
}
/* The board the ledger lies on. Without it the leaf floats on the shell's flat
   wash, which is the one surface on these screens that is still a CSS box. */
.hm-hof__book {
  /* flex-grow here grows the book along the body's MAIN axis, which is
     horizontal, and it swallowed the width cap - the ledger spread to nearly
     the full 1920 rather than the 1120 asked for. The height comes from
     align-items:stretch on the cross axis instead, which is what was wanted. */
  flex: 0 1 auto;
  display: flex;
  /* 1120 left 410px of bare wood down each side of a 1920 window - the leaf
     spanned x410 to x1510 on the shipped shot - which is a laptop layout
     floated in a desktop frame. There is nothing else on this screen for the
     margin to separate the ledger from.
     1560 was the same argument stopped one step early: it still left 180px of
     board down each side at 1920, and that board is a 6px hatch, which is the
     one surface that satisfies a texture-scale blank scan while being nothing
     to look at. Measured on the coarse scan that ignores texture, those two
     margins were 18.8 of the frame's 26.6 per cent of dead area - the single
     largest thing wrong with the screen after the empty page itself. */
  width: min(1860px, 97%);
  margin: 0 auto;
  padding: 10px;
  background-color: #2a1408;
  background-image:
    repeating-linear-gradient(126deg, rgba(255,220,170,0.045) 0 2px, rgba(0,0,0,0.10) 2px 5px),
    radial-gradient(120% 130% at 50% 0%, #5a2f14 0%, #34190a 55%, #1c0d04 100%);
  box-shadow:
    inset 0 0 0 1px rgba(0,0,0,0.9),
    inset 0 0 0 3px #c8a052,
    inset 0 0 0 4px rgba(0,0,0,0.85),
    inset 0 0 46px rgba(0,0,0,0.6),
    0 18px 56px rgba(0,0,0,0.9);
}
/* The ruled leaf itself. */
.hm-hof__leaf {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 18px 26px 14px;
  color: #2c2008;
  /* Aged, and with a tooth.
     This leaf shipped at radial(#f2e3bd -> #dcc697 -> #b79f72) with a 0.045
     hairline over it, and measured 151.3 mean luminance on the 1920x1080 shot
     against a median of about 62 across the build's twenty-seven scenes - the
     brightest frame in the game by fifty levels, on the one screen that is a
     sheet of paper. Two things were wrong and only one of them was the tint:
     the fibre was three luminance levels deep, so a 32x32 block of paper
     between two rules measured as FLAT, and a page with no tooth in it is a
     rectangle of colour rather than parchment. Aged one step and given a real
     laid grain in both directions, it reads as older paper and stops being the
     brightest object in the build. */
  background-image:
    repeating-linear-gradient(91deg,
      rgba(104,76,34,0.13) 0 1px,
      rgba(255,248,222,0.085) 1px 2px,
      rgba(0,0,0,0) 2px 5px),
    repeating-linear-gradient(2deg,
      rgba(104,76,34,0.075) 0 1px,
      rgba(255,248,222,0.05) 1px 2px,
      rgba(0,0,0,0) 2px 9px),
    radial-gradient(80% 60% at 88% 96%, rgba(120,86,38,0.36) 0%, rgba(0,0,0,0) 60%),
    radial-gradient(120% 140% at 50% 12%, #cdb385 0%, #ab9061 46%, #7e6840 100%);
  box-shadow:
    inset 0 0 0 1px rgba(70,52,26,0.7),
    inset 0 0 60px rgba(120,88,42,0.24);
}
.hm-hof__head {
  flex: 0 0 auto;
  font-family: var(--hm-font-display, 'Palatino Linotype', 'Book Antiqua', Palatino, Georgia, serif);
  font-size: max(clamp(17px, 1.5vw, 27px), var(--hm-type-floor));
  letter-spacing: 0.18em;
  text-transform: uppercase;
  text-align: center;
  color: #4a3410;
  padding-bottom: 9px;
  text-shadow: 0 1px 0 rgba(255,246,215,0.55);
  background-image: linear-gradient(90deg,
    rgba(90,66,26,0) 0%, rgba(90,66,26,0.75) 12%, rgba(90,66,26,0.75) 88%, rgba(90,66,26,0) 100%);
  background-size: 100% 1px;
  background-position: 0 100%;
  background-repeat: no-repeat;
}
/* Column heads. Small caps in ink, on the ruling rather than on a bar. */
.hm-hof__cols {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 0 8px 5px;
  font-family: var(--hm-font-display, 'Palatino Linotype', 'Book Antiqua', Palatino, Georgia, serif);
  font-size: max(11px, var(--hm-type-floor));
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #6d5017;
  text-shadow: 0 1px 0 rgba(255,248,222,0.6);
}
.hm-hof__col--rank { width: 58px; flex: 0 0 auto; }
.hm-hof__col--score { width: 132px; flex: 0 0 auto; text-align: right; }
.hm-hof__col--game { flex: 1 1 auto; min-width: 0; padding-left: 18px; }
.hm-hof__col--date { flex: 0 0 auto; }

/* The ruled area. The lines continue past the last entry, because a ledger with
   six names in it is a page with room for more, not a page that stops. The row
   height is fixed so the ruling and the rows are the same 54px grid by
   construction rather than by coincidence. */
.hm-hof__rows {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  background-image: repeating-linear-gradient(180deg,
    rgba(0,0,0,0) 0 53px,
    rgba(90,66,26,0.30) 53px 54px);
}
/* ...but not behind the empty page's own copy. The ruling steps 54px and
   '.hm-hof__emptybody' sets its lines at clamp(13px,1vw,17px) x 1.65, which is
   28px at 1920x1080: measured there, the rules fell at y484/538/592/646 and
   the paragraph's lines at y590-597, 618-625 and 646-653, so the rules at 592
   and 646 ran through the x-height of two of the four lines. The ruling moves
   onto the space above and below the writing instead - a ledger with a note
   across it has clear paper where the note is - which needs no agreement
   between a decoration's pitch and a paragraph's leading. */
.hm-hof__rows--empty { background-image: none; }
.hm-hof__rule {
  flex: 1 1 auto;
  align-self: stretch;
  min-height: 0;
}
/* Each band rules outward from the writing rather than downward from the top
   of the page, so the two halves are symmetric about it instead of meeting at
   an arbitrary phase: the upper band's gradient runs bottom-to-top. */
.hm-hof__rule--over {
  background-image: repeating-linear-gradient(0deg,
    rgba(0,0,0,0) 0 53px,
    rgba(90,66,26,0.30) 53px 54px);
}
.hm-hof__rule--under {
  background-image: repeating-linear-gradient(180deg,
    rgba(0,0,0,0) 0 53px,
    rgba(90,66,26,0.30) 53px 54px);
}
/* One ruled line. Alternating tint plus a hairline rule is how a ledger is
   read across; a stack of dark slabs is how a settings dialog is. */
.hm-hof__row {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 14px;
  height: 54px;
  padding: 0 8px;
  background-image: linear-gradient(90deg,
    rgba(90,66,26,0) 0%, rgba(90,66,26,0.55) 4%, rgba(90,66,26,0.55) 96%, rgba(90,66,26,0) 100%);
  background-size: 100% 1px;
  background-position: 0 100%;
  background-repeat: no-repeat;
}
.hm-hof__row:nth-child(odd) { background-color: rgba(255,248,222,0.30); }
.hm-hof__row--you { background-color: rgba(255,236,170,0.62);
  box-shadow: inset 0 0 0 2px #a8801f; }

/* The rank medallion: struck bronze on the page, and gold, silver and copper
   for the first three so a ranked table ranks something visually. */
.hm-hof__rank {
  flex: 0 0 auto;
  width: 38px; height: 38px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  color: #fdf1cf;
  background-color: #6a4d1c;
  background-image:
    radial-gradient(62% 58% at 34% 24%, rgba(255,244,214,0.55) 0%, rgba(255,244,214,0) 62%),
    linear-gradient(168deg, #a9853c 0%, #74551f 52%, #3f2d0e 100%);
  box-shadow:
    inset 0 0 0 1px rgba(38,26,6,0.85),
    inset 0 1px 0 rgba(255,242,206,0.45),
    0 1px 0 rgba(255,252,235,0.6),
    0 2px 5px rgba(88,64,26,0.5);
  text-shadow: 0 1px 0 rgba(0,0,0,0.55);
  margin-left: 10px;
}
.hm-hof__rank--1 {
  color: #3a2a06;
  background-image:
    radial-gradient(62% 58% at 34% 24%, rgba(255,255,236,0.85) 0%, rgba(255,255,236,0) 60%),
    linear-gradient(168deg, #ffe9a8 0%, #d9ac3b 50%, #8c6410 100%);
  box-shadow:
    inset 0 0 0 1px rgba(58,42,6,0.9), inset 0 1px 0 rgba(255,255,240,0.85),
    0 0 14px rgba(217,178,90,0.55), 0 2px 5px rgba(88,64,26,0.5);
  text-shadow: 0 1px 0 rgba(255,250,225,0.6);
}
.hm-hof__rank--2 {
  color: #2b2b30;
  background-image:
    radial-gradient(62% 58% at 34% 24%, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0) 60%),
    linear-gradient(168deg, #eef1f4 0%, #b3bcc4 52%, #6d757d 100%);
  box-shadow:
    inset 0 0 0 1px rgba(40,44,48,0.85), inset 0 1px 0 rgba(255,255,255,0.85),
    0 2px 5px rgba(60,64,70,0.45);
  text-shadow: 0 1px 0 rgba(255,255,255,0.55);
}
.hm-hof__rank--3 {
  color: #331d0c;
  background-image:
    radial-gradient(62% 58% at 34% 24%, rgba(255,226,196,0.7) 0%, rgba(255,226,196,0) 60%),
    linear-gradient(168deg, #e3a473 0%, #b06a34 52%, #6a3a15 100%);
  box-shadow:
    inset 0 0 0 1px rgba(51,29,12,0.85), inset 0 1px 0 rgba(255,232,206,0.7),
    0 2px 5px rgba(88,50,26,0.45);
  text-shadow: 0 1px 0 rgba(255,232,206,0.4);
}
.hm-hof__flag {
  flex: 0 0 auto;
  width: 10px; height: 26px;
  box-shadow: inset 0 0 0 1px rgba(0,0,0,0.75), 0 1px 0 rgba(255,248,222,0.5);
  clip-path: polygon(0% 0%, 100% 0%, 100% 100%, 50% 78%, 0% 100%);
}
.hm-hof__score {
  flex: 0 0 auto;
  width: 132px;
  display: flex;
  justify-content: flex-end;
  color: #2c2008;
}
/* A lost game is still on the board; it is written in a fainter hand. */
.hm-hof__score--defeat { color: #6b4e33; }
.hm-hof__score--draw { color: #4f4327; }
.hm-hof__game {
  flex: 1 1 auto;
  min-width: 0;
  padding-left: 18px;
  font-size: max(clamp(12px, 0.85vw, 15px), var(--hm-type-floor));
  color: #4a3a1c;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.hm-hof__date {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  color: #6a512a;
}
/* The empty page. It sits INSIDE the ruled area rather than replacing it, so a
   fresh browser sees a ledger nobody has written in - which is what it is -
   instead of a panel that failed to load. */
.hm-hof__empty {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  text-align: center;
}
/* ---- the frieze.
   Twelve keeps drawn at exactly half the packed size - 130px of box against a
   225x242 frame gives fitSpriteBox a divisor of 2, so the sample grid stays
   whole and the band is 112px wide per house. Aligned on their feet, because
   the pack draws all twelve standing on the same ground line and lining them
   up any other way throws that away. */
.hm-hof__crests {
  flex: 0 0 auto;
  align-self: stretch;
  display: grid;
  /* Six to a row, twelve houses, two rows. A wrapping flex row gives whatever
     count the width happens to allow, which is 11-and-1 at some sizes; the
     band is a composition, so the count is declared. */
  grid-template-columns: repeat(6, minmax(0, 1fr));
  align-items: stretch;
  justify-items: stretch;
  gap: 5px;
  padding: 2px 4px 6px;
  /* The ground the twelve stand on: one ruled line under the whole band, the
     same ink the ledger's own rules are drawn in. */
  background-image: linear-gradient(90deg,
    rgba(90,66,26,0) 0%, rgba(90,66,26,0.55) 6%, rgba(90,66,26,0.55) 94%, rgba(90,66,26,0) 100%);
  background-size: 100% 1px;
  background-position: 0 100%;
  background-repeat: no-repeat;
}
/* Each house in a cut tile rather than loose on the page.
   Twelve keeps set straight on parchment left 100px-wide bands of untouched
   paper between them - a picture that is twelve pictures with nothing holding
   them - and this is the same grid of framed tiles the new game screen picks a
   faction with, which is where a player has already met all twelve. The tile
   also gives each one its name, which loose sprites could not. */
.hm-hof__crest {
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 3px;
  padding: 4px 3px 4px;
  background-image:
    radial-gradient(74% 56% at 50% 8%, rgba(120,88,38,0.16) 0%, rgba(0,0,0,0) 70%),
    linear-gradient(178deg, rgba(90,66,26,0.10) 0%, rgba(90,66,26,0.26) 100%);
  box-shadow:
    inset 0 0 0 1px rgba(70,52,26,0.55),
    inset 0 1px 0 rgba(255,248,222,0.55);
}
.hm-hof__crestname {
  font-family: var(--hm-font-display, 'Palatino Linotype', 'Book Antiqua', Palatino, Georgia, serif);
  font-size: max(10px, var(--hm-type-floor));
  letter-spacing: 0.16em;
  text-transform: uppercase;
  /* #5d4413 measured 2.5:1 on the crest cell's tan (ink 93,68,19 on 153,127,82); #2e2008 was 4.16 there.
     Near-black ink, same engraved highlight: 5.0. */
  color: #140e04;
  text-shadow: 0 1px 0 rgba(255,248,222,0.55);
}
.hm-hof__crestcap {
  flex: 0 0 auto;
  margin: 0;
  font-family: var(--hm-font-display, 'Palatino Linotype', 'Book Antiqua', Palatino, Georgia, serif);
  font-size: max(11.5px, var(--hm-type-floor));
  letter-spacing: 0.13em;
  text-transform: uppercase;
  color: #2e2008; /* was #6d5017, 2.8:1 */
  text-shadow: 0 1px 0 rgba(255,248,222,0.6);
}
/* A keep on a written row, cut into the page the way the rank medallion is. */
.hm-hof__seat {
  flex: 0 0 auto;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  width: 56px;
  height: 50px;
}
@media (max-height: 880px) {
  .hm-hof__crests { padding-bottom: 5px; }
}
/* No wash behind the words. A pool of lighter paper under a paragraph reads as
   a halo, and the rules are faint enough that a line of text sitting on them is
   what writing on a ruled page looks like. */
.hm-hof__emptyhead {
  font-family: var(--hm-font-display, 'Palatino Linotype', 'Book Antiqua', Palatino, Georgia, serif);
  font-size: max(clamp(18px, 1.6vw, 28px), var(--hm-type-floor));
  letter-spacing: 0.1em;
  color: #2e2008; /* was #4a3410, 4.36:1 */
  text-shadow: 0 1px 0 rgba(255,246,215,0.55);
}
.hm-hof__emptybody {
  max-width: 96ch;
  margin: 0;
  font-family: var(--hm-font-display, 'Palatino Linotype', 'Book Antiqua', Palatino, Georgia, serif);
  font-size: max(clamp(13px, 1vw, 17px), var(--hm-type-floor));
  line-height: 1.65;
  color: #33240e; /* was #4a3a1c, 3.8:1 */
  text-shadow: 0 1px 0 rgba(255,248,222,0.5);
}
/* The plank's one label, struck into the wood like every other small caption
   on this build rather than set loose beside it. */
.hm-hof__note {
  flex: 0 0 auto;
  padding: 3px 10px 4px;
  font-family: var(--hm-font-display, 'Palatino Linotype', 'Book Antiqua', Palatino, Georgia, serif);
  font-size: max(11px, var(--hm-type-floor));
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #d2b98c;
  white-space: nowrap;
  background-image: linear-gradient(180deg, rgba(0,0,0,0.42), rgba(0,0,0,0.16));
  box-shadow:
    inset 0 0 0 1px rgba(0,0,0,0.8),
    inset 0 1px 0 rgba(255,232,186,0.16),
    0 1px 0 rgba(255,232,186,0.08);
  text-shadow: 0 1px 0 rgba(0,0,0,0.9);
}

/* ---- the ladder, printed on an empty page.
   Three ruled columns of the multipliers the formula actually pays. The figures
   are set in the bitmap face for the same reason the scores are: a ledger is
   read down its numeral column, and a serif 1.60 beside a struck 13,500 is two
   programs in one frame. */
.hm-hof__ladder {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0 40px;
  width: 100%;
  margin-top: 4px;
}
.hm-hof__lcol { display: flex; flex-direction: column; min-width: 0; }
.hm-hof__lhead {
  font-family: var(--hm-font-display, 'Palatino Linotype', 'Book Antiqua', Palatino, Georgia, serif);
  font-size: max(11px, var(--hm-type-floor));
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #6d5017;
  text-align: left;
  padding-bottom: 5px;
  text-shadow: 0 1px 0 rgba(255,248,222,0.6);
  background-image: linear-gradient(90deg, rgba(90,66,26,0.75) 0%, rgba(90,66,26,0.75) 100%);
  background-size: 100% 1px;
  background-position: 0 100%;
  background-repeat: no-repeat;
}
/* One entry, ruled under. The rule is the row's own, so the ladder cannot fall
   out of step with a decoration pitched somewhere else. */
/* Alternating bands, the same as the written rows above. A ruled line is one
   pixel and a page is read at arm's length: three ruled rows down a 600px
   column left the whole column measuring as blank paper at any scale coarser
   than the rule itself, which is every scale a person looks at it from. */
.hm-hof__lrow:nth-child(even) { background-color: rgba(255,248,222,0.34); }
.hm-hof__lrow:nth-child(odd) { background-color: rgba(120,88,38,0.10); }
.hm-hof__lrow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 7px 2px 6px;
  background-image: linear-gradient(90deg, rgba(90,66,26,0.34) 0%, rgba(90,66,26,0.34) 100%);
  background-size: 100% 1px;
  background-position: 0 100%;
  background-repeat: no-repeat;
}
.hm-hof__llabel {
  font-family: var(--hm-font-display, 'Palatino Linotype', 'Book Antiqua', Palatino, Georgia, serif);
  font-size: max(clamp(12px, 0.95vw, 16px), var(--hm-type-floor));
  color: #4a3a1c;
  text-shadow: 0 1px 0 rgba(255,248,222,0.5);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.hm-hof__lfactor { display: flex; align-items: center; gap: 4px; color: #2c2008; }
.hm-hof__ltimes {
  font-family: var(--hm-font-display, 'Palatino Linotype', 'Book Antiqua', Palatino, Georgia, serif);
  font-size: max(13px, var(--hm-type-floor));
  color: #6a512a;
}
/* One line, not two. The leaf is 1800px wide now and this sentence is 90
   characters; wrapping it at 72 put its second half on a line the page had no
   room for, and the page scrolls, so what a player saw was "= " with the total
   cut off below the fold. */
.hm-hof__example {
  max-width: none;
  margin: 2px 0 0;
  font-family: var(--hm-font-display, 'Palatino Linotype', 'Book Antiqua', Palatino, Georgia, serif);
  font-size: max(clamp(12px, 0.9vw, 15px), var(--hm-type-floor));
  font-style: italic;
  color: #1e1608; /* was #5a4726, 2.5:1 on the darker rule band */
  text-shadow: 0 1px 0 rgba(255,248,222,0.5);
}
@media (max-height: 880px) {
  .hm-hof__ladder { gap: 0 24px; }
  .hm-hof__lrow { padding: 4px 2px 4px; }
  .hm-hof__empty { gap: 7px; }
}
.hm-hof__foot {
  flex: 0 0 auto;
  padding-top: 8px;
  text-align: center;
  font-family: var(--hm-font-display, 'Palatino Linotype', 'Book Antiqua', Palatino, Georgia, serif);
  font-size: max(12px, var(--hm-type-floor));
  font-style: italic;
  letter-spacing: 0.06em;
  color: #1e1608; /* was #6a512a, 1.95:1 */
  background-image: linear-gradient(90deg,
    rgba(90,66,26,0) 0%, rgba(90,66,26,0.6) 12%, rgba(90,66,26,0.6) 88%, rgba(90,66,26,0) 100%);
  background-size: 100% 1px;
  background-position: 0 0;
  background-repeat: no-repeat;
}
`;

const HALL_SHEET = `${FORM_ART_CSS}\n${HALL_CSS}`;

function useHallStyles(): void {
  useStyleSheet(HALL_STYLE_ID, HALL_SHEET, StyleLayer.screen);
}
